import * as THREE from 'three';
import type { CoinPusherMachineRuntime } from '../../assets/machine';

export type VisualPolishQuality = 'full' | 'performance';

export interface VisualPolishOptions {
  readonly quality?: VisualPolishQuality;
  readonly tuneMaterials?: boolean;
  readonly addInteriorLights?: boolean;
  /** Front glazing opacity. Defaults to 0.24. */
  readonly glassOpacity?: number;
  /** Environment response multiplier for chrome. Defaults to 1.6. */
  readonly chromeEnvironmentIntensity?: number;
}

export interface VisualPolishMetadata {
  readonly version: 1;
  readonly quality: VisualPolishQuality;
  readonly lightBudget: Readonly<{
    maximumAddedLights: 3;
    shadowCastingAddedLights: 0;
  }>;
  readonly materialRoles: readonly [
    'glass',
    'chrome-and-steel',
    'coins',
    'cards-and-prizes',
  ];
  readonly dynamicObjectPolicy: 'call refreshVisibility after spawning new coin/card visuals';
}

export interface VisualPolishDiagnostics {
  readonly quality: VisualPolishQuality;
  readonly materialsTuned: number;
  readonly glassMeshes: number;
  readonly chromeMeshes: number;
  readonly coinMeshes: number;
  readonly cardMeshes: number;
  readonly existingSceneLights: number;
  readonly addedLights: number;
  readonly totalSceneLights: number;
  readonly totalShadowCastingLights: number;
}

interface ObjectRenderState {
  readonly renderOrder: number;
  readonly castShadow: boolean;
  readonly receiveShadow: boolean;
}

const LIGHT_RIG_NAME = 'visualPolish.machineInteriorLights';

/**
 * Tunes named machine/scene surfaces and installs one bounded interior rig.
 * It does not own the scene environment or broad scene lighting.
 */
export class VisualPolish {
  readonly metadata: VisualPolishMetadata;

  private readonly quality: VisualPolishQuality;
  private readonly tuneMaterials: boolean;
  private readonly glassOpacity: number;
  private readonly chromeEnvironmentIntensity: number;
  private readonly materialSnapshots = new Map<THREE.Material, THREE.Material>();
  private readonly objectSnapshots = new Map<THREE.Object3D, ObjectRenderState>();
  private readonly glassMeshes = new Set<THREE.Object3D>();
  private readonly chromeMeshes = new Set<THREE.Object3D>();
  private readonly coinMeshes = new Set<THREE.Object3D>();
  private readonly cardMeshes = new Set<THREE.Object3D>();
  private readonly existingSceneLights: number;
  private lightRoot: THREE.Group | null = null;
  private interiorKey: THREE.SpotLight | null = null;
  private interiorRim: THREE.SpotLight | null = null;
  private trayPractical: THREE.PointLight | null = null;
  private interiorKeyBase = 0;
  private interiorRimBase = 0;
  private trayPracticalBase = 0;
  private disposed = false;

  constructor(
    readonly scene: THREE.Scene,
    readonly machineRuntime: CoinPusherMachineRuntime,
    options: VisualPolishOptions = {},
  ) {
    this.quality = options.quality ?? 'full';
    this.tuneMaterials = options.tuneMaterials ?? true;
    this.glassOpacity = readRange(options.glassOpacity ?? 0.24, 0.08, 0.5, 'glassOpacity');
    this.chromeEnvironmentIntensity = readRange(
      options.chromeEnvironmentIntensity ?? 1.6,
      0.5,
      2.5,
      'chromeEnvironmentIntensity',
    );
    this.existingSceneLights = countLights(scene);
    this.metadata = Object.freeze({
      version: 1 as const,
      quality: this.quality,
      lightBudget: Object.freeze({
        maximumAddedLights: 3 as const,
        shadowCastingAddedLights: 0 as const,
      }),
      materialRoles: Object.freeze([
        'glass',
        'chrome-and-steel',
        'coins',
        'cards-and-prizes',
      ] as const),
      dynamicObjectPolicy: 'call refreshVisibility after spawning new coin/card visuals' as const,
    });

    if (this.tuneMaterials) this.tuneNamedMachineMaterials();
    this.refreshVisibility(scene);
    if (options.addInteriorLights ?? true) this.createInteriorLightRig();
  }

  /**
   * Applies visibility/render-order policy to newly spawned physics coins and
   * bonus cards. It is idempotent and need only run after spawn batches.
   */
  refreshVisibility(root: THREE.Object3D = this.scene): VisualPolishDiagnostics {
    this.assertActive();
    root.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      const name = object.name.toLowerCase();
      const materialNames = materials.map((material) => material.name.toLowerCase());
      const isGlass =
        name.includes('glass') ||
        materials.includes(this.machineRuntime.materials.glass) ||
        materialNames.some((materialName) => materialName.includes('glass'));
      const isChrome =
        materials.includes(this.machineRuntime.materials.chrome) ||
        materials.includes(this.machineRuntime.materials['brushed-steel']) ||
        materialNames.some(
          (materialName) => materialName.includes('chrome') || materialName.includes('brushedsteel'),
        );
      const isCoin = isCoinObject(object);
      const isCard = isCardObject(object);

      if (isGlass) {
        this.rememberObject(object);
        this.glassMeshes.add(object);
        object.renderOrder = 40;
        object.castShadow = false;
        object.receiveShadow = false;
        if (this.tuneMaterials) {
          for (const material of materials) this.tuneGlassMaterial(material);
        }
      } else if (isCard) {
        this.rememberObject(object);
        this.cardMeshes.add(object);
        object.renderOrder = 12;
        object.receiveShadow = true;
        if (this.tuneMaterials) {
          for (const material of materials) this.tuneCardMaterial(material);
        }
      } else if (isCoin) {
        this.rememberObject(object);
        this.coinMeshes.add(object);
        object.renderOrder = 6;
        object.castShadow = false;
        object.receiveShadow = true;
        if (this.tuneMaterials) {
          for (const material of materials) this.tuneCoinMaterial(material);
        }
      }

      if (isChrome) {
        this.chromeMeshes.add(object);
        if (this.tuneMaterials) {
          for (const material of materials) this.tuneChromeMaterial(material);
        }
      }
    });
    return this.getDiagnostics();
  }

  /** Optional subtle practical-light motion; no new sources are created. */
  update(elapsedSeconds: number): void {
    if (this.disposed) return;
    const elapsed = Number.isFinite(elapsedSeconds) ? elapsedSeconds : 0;
    if (this.interiorKey) {
      this.interiorKey.intensity =
        this.interiorKeyBase + Math.sin(elapsed * 1.35) * this.interiorKeyBase * 0.024;
    }
    if (this.interiorRim) {
      this.interiorRim.intensity =
        this.interiorRimBase + Math.sin(elapsed * 1.12 + 1.8) * this.interiorRimBase * 0.026;
    }
    if (this.trayPractical) {
      this.trayPractical.intensity =
        this.trayPracticalBase + Math.sin(elapsed * 2.1 + 0.7) * this.trayPracticalBase * 0.042;
    }
  }

  getDiagnostics(): VisualPolishDiagnostics {
    return Object.freeze({
      quality: this.quality,
      materialsTuned: this.materialSnapshots.size,
      glassMeshes: this.glassMeshes.size,
      chromeMeshes: this.chromeMeshes.size,
      coinMeshes: this.coinMeshes.size,
      cardMeshes: this.cardMeshes.size,
      existingSceneLights: this.existingSceneLights,
      addedLights: this.lightRoot ? countLights(this.lightRoot) : 0,
      totalSceneLights: countLights(this.scene),
      totalShadowCastingLights: countLights(this.scene, true),
    });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.lightRoot?.removeFromParent();
    this.lightRoot?.clear();
    this.lightRoot = null;
    this.interiorKey = null;
    this.interiorRim = null;
    this.trayPractical = null;
    this.interiorKeyBase = 0;
    this.interiorRimBase = 0;
    this.trayPracticalBase = 0;

    for (const [object, state] of this.objectSnapshots) {
      object.renderOrder = state.renderOrder;
      object.castShadow = state.castShadow;
      object.receiveShadow = state.receiveShadow;
    }
    this.objectSnapshots.clear();

    for (const [material, snapshot] of this.materialSnapshots) {
      material.copy(snapshot);
      material.needsUpdate = true;
      snapshot.dispose();
    }
    this.materialSnapshots.clear();
    this.glassMeshes.clear();
    this.chromeMeshes.clear();
    this.coinMeshes.clear();
    this.cardMeshes.clear();
  }

  private tuneNamedMachineMaterials(): void {
    this.tuneGlassMaterial(this.machineRuntime.materials.glass);
    this.tuneChromeMaterial(this.machineRuntime.materials.chrome);
    this.tuneSteelMaterial(this.machineRuntime.materials['brushed-steel']);
    this.tuneCoinMaterial(this.machineRuntime.materials.nickel);
    this.tuneCoinMaterial(this.machineRuntime.materials.brass);
  }

  private tuneGlassMaterial(material: THREE.Material): void {
    if (!(material instanceof THREE.MeshPhysicalMaterial)) return;
    this.rememberMaterial(material);
    material.transparent = true;
    material.opacity = this.glassOpacity;
    // Physical transmission asks Three.js to render every opaque medal and
    // cabinet surface into a second scene buffer. Alpha glass keeps the authored
    // reflections, clearcoat and highlight cues without duplicating the dense
    // coin field.
    material.transmission = 0;
    material.roughness = 0.055;
    material.metalness = 0;
    material.thickness = 0.018;
    material.ior = 1.48;
    material.clearcoat = 1;
    material.clearcoatRoughness = 0.035;
    material.envMapIntensity = 1.28;
    material.attenuationColor.set('#bceef1');
    material.attenuationDistance = 3.8;
    material.depthWrite = false;
    material.depthTest = true;
    material.side = THREE.DoubleSide;
    material.forceSinglePass = true;
    material.needsUpdate = true;
  }

  private tuneChromeMaterial(material: THREE.Material): void {
    if (!(material instanceof THREE.MeshStandardMaterial)) return;
    this.rememberMaterial(material);
    material.metalness = Math.max(0.92, material.metalness);
    material.roughness = clamp(material.roughness, 0.11, 0.2);
    material.envMapIntensity = this.chromeEnvironmentIntensity;
    if (material instanceof THREE.MeshPhysicalMaterial) {
      material.clearcoat = Math.max(0.32, material.clearcoat);
      material.clearcoatRoughness = Math.min(0.12, material.clearcoatRoughness);
    }
    material.needsUpdate = true;
  }

  private tuneSteelMaterial(material: THREE.Material): void {
    if (!(material instanceof THREE.MeshStandardMaterial)) return;
    this.rememberMaterial(material);
    material.metalness = Math.max(0.86, material.metalness);
    material.roughness = clamp(material.roughness, 0.26, 0.38);
    material.envMapIntensity = 1.12;
    material.needsUpdate = true;
  }

  private tuneCoinMaterial(material: THREE.Material): void {
    if (!(material instanceof THREE.MeshStandardMaterial)) return;
    this.rememberMaterial(material);
    material.metalness = Math.max(0.88, material.metalness);
    material.roughness = clamp(material.roughness, 0.16, 0.3);
    material.envMapIntensity = Math.max(1.22, material.envMapIntensity);
    material.needsUpdate = true;
  }

  private tuneCardMaterial(material: THREE.Material): void {
    if (!(material instanceof THREE.MeshStandardMaterial)) return;
    this.rememberMaterial(material);
    material.envMapIntensity = Math.max(1.05, material.envMapIntensity);
    material.roughness = clamp(material.roughness, 0.16, 0.48);
    if (material instanceof THREE.MeshPhysicalMaterial) {
      material.clearcoat = Math.max(0.48, material.clearcoat);
      material.clearcoatRoughness = Math.min(0.18, material.clearcoatRoughness);
    }
    material.needsUpdate = true;
  }

  private rememberMaterial(material: THREE.Material): void {
    if (!this.materialSnapshots.has(material)) {
      this.materialSnapshots.set(material, material.clone());
    }
  }

  private rememberObject(object: THREE.Object3D): void {
    if (!this.objectSnapshots.has(object)) {
      this.objectSnapshots.set(object, {
        renderOrder: object.renderOrder,
        castShadow: object.castShadow,
        receiveShadow: object.receiveShadow,
      });
    }
  }

  private createInteriorLightRig(): void {
    const machineRoot = this.machineRuntime.nodes.root;
    if (machineRoot.getObjectByName(LIGHT_RIG_NAME)) return;
    const broadRigPresent = this.scene.getObjectByName('arcade.light.key') !== undefined;
    const broadRigScale = broadRigPresent ? 0.78 : 1;
    const qualityScale = this.quality === 'full' ? 1 : 0.74;
    const intensityScale = broadRigScale * qualityScale;

    const root = new THREE.Group();
    root.name = LIGHT_RIG_NAME;

    const key = new THREE.SpotLight(
      '#ffe1b0',
      7.4 * intensityScale,
      6.2,
      Math.PI * 0.28,
      0.82,
      1.7,
    );
    key.name = 'visualPolish.light.interiorKey';
    key.position.set(-1.35, 4.2, 1.35);
    key.target.position.set(0, 2.28, 0.28);
    key.castShadow = false;
    root.add(key, key.target);

    const rim = new THREE.SpotLight(
      '#4ce8f3',
      6.2 * intensityScale,
      6,
      Math.PI * 0.24,
      0.78,
      1.8,
    );
    rim.name = 'visualPolish.light.interiorRim';
    rim.position.set(1.55, 3.72, -0.82);
    rim.target.position.set(0.2, 2.32, 0.62);
    rim.castShadow = false;
    root.add(rim, rim.target);

    const practical = new THREE.PointLight(
      '#ffb64d',
      2.4 * intensityScale,
      3,
      2,
    );
    practical.name = 'visualPolish.light.trayPractical';
    practical.position.set(0, 1.48, 1.45);
    practical.castShadow = false;
    root.add(practical);

    machineRoot.add(root);
    this.lightRoot = root;
    this.interiorKey = key;
    this.interiorRim = rim;
    this.trayPractical = practical;
    this.interiorKeyBase = key.intensity;
    this.interiorRimBase = rim.intensity;
    this.trayPracticalBase = practical.intensity;
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('VisualPolish has been disposed.');
  }
}

export function createVisualPolish(
  scene: THREE.Scene,
  machineRuntime: CoinPusherMachineRuntime,
  options: VisualPolishOptions = {},
): VisualPolish {
  return new VisualPolish(scene, machineRuntime, options);
}

function isCoinObject(object: THREE.Object3D): boolean {
  const name = object.name.toLowerCase();
  return (
    name.startsWith('coins.') ||
    name === 'coinbody' ||
    name === 'coinrelief' ||
    name.includes(': bodies') ||
    name.includes(': reliefs') ||
    object.parent?.userData.coin !== undefined
  );
}

function isCardObject(object: THREE.Object3D): boolean {
  const assetType = object.userData.assetType;
  return (
    object.name.toLowerCase().startsWith('prize.') ||
    assetType === 'bonus-card-part' ||
    object.parent?.userData.assetType === 'bonus-card'
  );
}

function countLights(root: THREE.Object3D, shadowCastingOnly = false): number {
  let count = 0;
  root.traverse((object) => {
    if (object instanceof THREE.Light && (!shadowCastingOnly || object.castShadow)) count += 1;
  });
  return count;
}

function readRange(value: number, min: number, max: number, label: string): number {
  if (!Number.isFinite(value) || value < min || value > max) {
    throw new RangeError(`${label} must be a finite number from ${min} through ${max}.`);
  }
  return value;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
